
import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface WorkflowState {
  id: string;
  nombre: string;
  color: string;
  orden: number;
  documentosObligatorios: string[];
  camposObligatorios: string[];
  activo: boolean;
}

interface Transition {
  id: string;
  desde: string;
  hacia: string;
  requiereAprobacion: boolean;
  notificarCliente: boolean;
}

const initialStates: WorkflowState[] = [
  {
    id: '1',
    nombre: 'Creada',
    color: '#6B7280',
    orden: 1,
    documentosObligatorios: [],
    camposObligatorios: ['Cliente', 'Tipo Operación'],
    activo: true
  },
  {
    id: '2',
    nombre: 'Documentación',
    color: '#F59E0B',
    orden: 2,
    documentosObligatorios: ['Factura Comercial', 'Packing List'],
    camposObligatorios: ['Valor FOB', 'Incoterm'],
    activo: true
  },
  {
    id: '3',
    nombre: 'DUCA',
    color: '#2F80ED',
    orden: 3,
    documentosObligatorios: ['DUCA', 'Certificado de Origen'],
    camposObligatorios: ['Fracción Arancelaria'],
    activo: true
  },
  {
    id: '4',
    nombre: 'Selectivo',
    color: '#8B5CF6',
    orden: 4,
    documentosObligatorios: [],
    camposObligatorios: ['Resultado Selectivo'],
    activo: true
  },
  {
    id: '5',
    nombre: 'Liberada',
    color: '#10B981',
    orden: 5,
    documentosObligatorios: ['Comprobante de Pago'],
    camposObligatorios: [],
    activo: true
  },
  {
    id: '6',
    nombre: 'Transporte',
    color: '#0EA5E9',
    orden: 6,
    documentosObligatorios: ['Carta Porte'],
    camposObligatorios: ['Transportista', 'Placas'],
    activo: true
  },
  {
    id: '7',
    nombre: 'Entrega',
    color: '#14B8A6',
    orden: 7,
    documentosObligatorios: ['Acuse de Recibo'],
    camposObligatorios: ['Fecha Entrega Real'],
    activo: true
  },
  {
    id: '8',
    nombre: 'Cerrada',
    color: '#059669',
    orden: 8,
    documentosObligatorios: [],
    camposObligatorios: [],
    activo: true
  }
];

const initialTransitions: Transition[] = [
  { id: '1', desde: 'Creada', hacia: 'Documentación', requiereAprobacion: false, notificarCliente: true },
  { id: '2', desde: 'Documentación', hacia: 'DUCA', requiereAprobacion: false, notificarCliente: true },
  { id: '3', desde: 'DUCA', hacia: 'Selectivo', requiereAprobacion: true, notificarCliente: true },
  { id: '4', desde: 'Selectivo', hacia: 'Liberada', requiereAprobacion: true, notificarCliente: true },
  { id: '5', desde: 'Liberada', hacia: 'Transporte', requiereAprobacion: false, notificarCliente: true },
  { id: '6', desde: 'Transporte', hacia: 'Entrega', requiereAprobacion: false, notificarCliente: true },
  { id: '7', desde: 'Entrega', hacia: 'Cerrada', requiereAprobacion: true, notificarCliente: true }
];

export default function OperationsWorkflow() {
  const [states, setStates] = useState<WorkflowState[]>(initialStates);
  const [transitions] = useState<Transition[]>(initialTransitions);
  const [showNewStateModal, setShowNewStateModal] = useState(false);
  const [showEditStateModal, setShowEditStateModal] = useState(false);
  const [selectedState, setSelectedState] = useState<WorkflowState | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'estados' | 'transiciones' | 'flujo'>('estados');

  const displayToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const toggleStateActive = (id: string) => {
    setStates(prev => prev.map(s => 
      s.id === id ? { ...s, activo: !s.activo } : s
    ));
    displayToast('Estado actualizado correctamente');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Estados y Flujo de Trabajo</h1>
            <p className="text-sm text-[#6B7280] mt-1">Configuración del workflow operativo</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-history-line mr-2"></i>Historial
            </button>
            <button 
              onClick={() => setShowNewStateModal(true)}
              className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>Nuevo Estado
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('estados')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'estados'
                    ? 'border-[#2F80ED] text-[#2F80ED]'
                    : 'border-transparent text-[#6B7280] hover:text-[#0A2A43]'
                }`}
              >
                <i className="ri-list-check mr-2"></i>Estados
              </button>
              <button
                onClick={() => setActiveTab('transiciones')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'transiciones'
                    ? 'border-[#2F80ED] text-[#2F80ED]'
                    : 'border-transparent text-[#6B7280] hover:text-[#0A2A43]'
                }`}
              >
                <i className="ri-arrow-left-right-line mr-2"></i>Transiciones
              </button>
              <button
                onClick={() => setActiveTab('flujo')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'flujo'
                    ? 'border-[#2F80ED] text-[#2F80ED]'
                    : 'border-transparent text-[#6B7280] hover:text-[#0A2A43]'
                }`}
              >
                <i className="ri-flow-chart mr-2"></i>Vista de Flujo
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Estados Tab */}
            {activeTab === 'estados' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#6B7280]">
                    {states.filter(s => s.activo).length} estados activos de {states.length} totales
                  </p>
                  <button className="text-sm text-[#2F80ED] hover:underline cursor-pointer">
                    <i className="ri-sort-asc mr-1"></i>Reordenar
                  </button>
                </div>
                <div className="grid gap-4">
                  {states.map((state) => (
                    <div 
                      key={state.id} 
                      className={`bg-white border rounded-lg p-4 transition-all ${
                        state.activo ? 'border-gray-200' : 'border-gray-100 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: state.color }}
                            ></div>
                            <span className="text-sm font-medium text-[#6B7280]">#{state.orden}</span>
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-[#0A2A43]">{state.nombre}</h3>
                            <div className="flex items-center gap-4 mt-1">
                              {state.documentosObligatorios.length > 0 && (
                                <span className="text-xs text-[#6B7280]">
                                  <i className="ri-file-text-line mr-1"></i>
                                  {state.documentosObligatorios.length} docs requeridos
                                </span>
                              )}
                              {state.camposObligatorios.length > 0 && (
                                <span className="text-xs text-[#6B7280]">
                                  <i className="ri-input-field mr-1"></i>
                                  {state.camposObligatorios.length} campos requeridos
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleStateActive(state.id)}
                            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                              state.activo ? 'bg-[#10B981]' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              state.activo ? 'left-7' : 'left-1'
                            }`}></div>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedState(state);
                              setShowEditStateModal(true);
                            }}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer"
                          >
                            <i className="ri-edit-line text-[#6B7280]"></i>
                          </button>
                          <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                            <i className="ri-delete-bin-line text-[#6B7280]"></i>
                          </button>
                        </div>
                      </div>
                      {(state.documentosObligatorios.length > 0 || state.camposObligatorios.length > 0) && (
                        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                          {state.documentosObligatorios.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-[#6B7280] mb-2">Documentos Obligatorios</p>
                              <div className="flex flex-wrap gap-2">
                                {state.documentosObligatorios.map((doc, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-[#EAF2F8] text-[#0A2A43] text-xs rounded">
                                    {doc}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {state.camposObligatorios.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-[#6B7280] mb-2">Campos Obligatorios</p>
                              <div className="flex flex-wrap gap-2">
                                {state.camposObligatorios.map((campo, idx) => (
                                  <span key={idx} className="px-2 py-1 bg-gray-100 text-[#6B7280] text-xs rounded">
                                    {campo}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transiciones Tab */}
            {activeTab === 'transiciones' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#6B7280]">{transitions.length} transiciones configuradas</p>
                  <button className="px-4 py-2 bg-[#EAF2F8] text-[#0A2A43] rounded-md text-sm font-medium hover:bg-[#d5e6f5] cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line mr-1"></i>Nueva Transición
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Desde</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase"></th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase">Hacia</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Requiere Aprobación</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-[#6B7280] uppercase">Notificar Cliente</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-[#6B7280] uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {transitions.map((trans) => {
                        const fromState = states.find(s => s.nombre === trans.desde);
                        const toState = states.find(s => s.nombre === trans.hacia);
                        return (
                          <tr key={trans.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: fromState?.color }}
                                ></div>
                                <span className="text-sm font-medium text-[#0A2A43]">{trans.desde}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <i className="ri-arrow-right-line text-[#6B7280]"></i>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: toState?.color }}
                                ></div>
                                <span className="text-sm font-medium text-[#0A2A43]">{trans.hacia}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              {trans.requiereAprobacion ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] text-xs rounded-full">
                                  <i className="ri-shield-check-line"></i>Sí
                                </span>
                              ) : (
                                <span className="text-xs text-[#6B7280]">No</span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {trans.notificarCliente ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#10B981]/10 text-[#10B981] text-xs rounded-full">
                                  <i className="ri-notification-line"></i>Sí
                                </span>
                              ) : (
                                <span className="text-xs text-[#6B7280]">No</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                                  <i className="ri-edit-line text-[#6B7280]"></i>
                                </button>
                                <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                                  <i className="ri-delete-bin-line text-[#6B7280]"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Flujo Visual Tab */}
            {activeTab === 'flujo' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#6B7280]">Visualización del flujo de trabajo</p>
                  <button className="px-4 py-2 bg-[#EAF2F8] text-[#0A2A43] rounded-md text-sm font-medium hover:bg-[#d5e6f5] cursor-pointer whitespace-nowrap">
                    <i className="ri-download-line mr-1"></i>Exportar Diagrama
                  </button>
                </div>
                <div className="bg-gray-50 rounded-lg p-8">
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {states.filter(s => s.activo).map((state, index) => (
                      <div key={state.id} className="flex items-center">
                        <div 
                          className="px-4 py-3 rounded-lg text-white text-sm font-medium shadow-md"
                          style={{ backgroundColor: state.color }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs opacity-75">#{state.orden}</span>
                            <span>{state.nombre}</span>
                          </div>
                        </div>
                        {index < states.filter(s => s.activo).length - 1 && (
                          <div className="flex items-center mx-2">
                            <div className="w-8 h-0.5 bg-gray-300"></div>
                            <i className="ri-arrow-right-s-line text-gray-400"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#EAF2F8] rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-[#2F80ED] text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-[#0A2A43]">Flujo de Operaciones</p>
                      <p className="text-sm text-[#6B7280] mt-1">
                        Las operaciones siguen este flujo secuencial. Cada transición puede requerir aprobación 
                        y notificar automáticamente al cliente según la configuración establecida.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Nuevo Estado */}
      {showNewStateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Nuevo Estado</h2>
              <button onClick={() => setShowNewStateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Estado *</label>
                <input 
                  type="text" 
                  placeholder="Ej: En Revisión" 
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color *</label>
                <div className="flex items-center gap-3">
                  {['#6B7280', '#F59E0B', '#2F80ED', '#8B5CF6', '#10B981', '#0EA5E9', '#14B8A6', '#EF4444'].map((color) => (
                    <button
                      key={color}
                      className="w-8 h-8 rounded-full border-2 border-white shadow-md cursor-pointer hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                    ></button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Posición en el Flujo *</label>
                <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]">
                  <option>Al final del flujo</option>
                  <option>Después de: Creada</option>
                  <option>Después de: Documentación</option>
                  <option>Después de: DUCA</option>
                  <option>Después de: Selectivo</option>
                  <option>Después de: Liberada</option>
                  <option>Después de: Transporte</option>
                  <option>Después de: Entrega</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Documentos Obligatorios</label>
                <div className="flex flex-wrap gap-2">
                  {['Factura Comercial', 'DUCA', 'BL/AWB', 'Certificado de Origen', 'Packing List', 'Permiso Sanitario'].map((doc) => (
                    <label key={doc} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100">
                      <input type="checkbox" className="rounded border-gray-300" />
                      <span className="text-sm text-[#6B7280]">{doc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campos Obligatorios</label>
                <div className="flex flex-wrap gap-2">
                  {['Cliente', 'Valor FOB', 'Incoterm', 'Fracción Arancelaria', 'Transportista', 'Fecha Entrega'].map((campo) => (
                    <label key={campo} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100">
                      <input type="checkbox" className="rounded border-gray-300" />
                      <span className="text-sm text-[#6B7280]">{campo}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowNewStateModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button 
                onClick={() => {
                  setShowNewStateModal(false);
                  displayToast('Estado creado exitosamente');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
              >
                Crear Estado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Estado */}
      {showEditStateModal && selectedState && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Editar Estado: {selectedState.nombre}</h2>
              <button onClick={() => setShowEditStateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-[#6B7280]"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del Estado *</label>
                <input 
                  type="text" 
                  defaultValue={selectedState.nombre}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2F80ED]" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color *</label>
                <div className="flex items-center gap-3">
                  {['#6B7280', '#F59E0B', '#2F80ED', '#8B5CF6', '#10B981', '#0EA5E9', '#14B8A6', '#EF4444'].map((color) => (
                    <button
                      key={color}
                      className={`w-8 h-8 rounded-full border-2 shadow-md cursor-pointer hover:scale-110 transition-transform ${
                        color === selectedState.color ? 'border-[#0A2A43]' : 'border-white'
                      }`}
                      style={{ backgroundColor: color }}
                    ></button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Documentos Obligatorios</label>
                <div className="flex flex-wrap gap-2">
                  {['Factura Comercial', 'DUCA', 'BL/AWB', 'Certificado de Origen', 'Packing List', 'Permiso Sanitario'].map((doc) => (
                    <label key={doc} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300" 
                        defaultChecked={selectedState.documentosObligatorios.includes(doc)}
                      />
                      <span className="text-sm text-[#6B7280]">{doc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campos Obligatorios</label>
                <div className="flex flex-wrap gap-2">
                  {['Cliente', 'Valor FOB', 'Incoterm', 'Fracción Arancelaria', 'Transportista', 'Fecha Entrega'].map((campo) => (
                    <label key={campo} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300" 
                        defaultChecked={selectedState.camposObligatorios.includes(campo)}
                      />
                      <span className="text-sm text-[#6B7280]">{campo}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowEditStateModal(false)} className="px-4 py-2.5 border border-gray-200 text-[#6B7280] rounded-md hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button 
                onClick={() => {
                  setShowEditStateModal(false);
                  displayToast('Estado actualizado correctamente');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md hover:shadow-md cursor-pointer whitespace-nowrap"
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 bg-[#10B981] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-pulse">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="font-medium">{toastMessage}</span>
        </div>
      )}
    </Layout>
  );
}
